import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for lazy-loaded routes
 * Catches chunk load failures and provides recovery UI
 */
export class LazyLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[LazyLoad] Failed to load route chunk:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div
            style={{
              padding: '2rem',
              textAlign: 'center',
              maxWidth: '600px',
              margin: '4rem auto',
            }}
          >
            <h2 style={{ marginBottom: '1rem' }}>Failed to Load Page</h2>
            <p
              style={{
                marginBottom: '2rem',
                color: 'var(--text-secondary)',
              }}
            >
              There was an error loading this page. This usually happens due to a network issue or outdated cache.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                backgroundColor: 'var(--primary-color)',
                color: 'var(--on-primary-color)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Refresh Page
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
